import { Suspense } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { postContactArchive } from 'api/contacts/single';
import * as ArchiveContact from 'assets/js/pages/contacts/ArchiveContact';
import * as DeleteContact from 'assets/js/pages/contacts/DeleteContact';
import * as MergeContactDialog from 'assets/js/pages/contacts/MergeContactDialog';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Tooltip from 'components/tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { accountQueryKeys } from 'queries/account-query-keys';

const AddTransaction = () => {
    const history = useHistory();
    return (
        <DropdownMenu.Item
            onClick={() => {
                history.push(`${location.pathname}/transactions/new`);
            }}
        >
            Add transaction
        </DropdownMenu.Item>
    );
};

const ExportTransactions = ({ onSelect }: { onSelect?: () => void }) => {
    const { data: contact } = useCurrentContact();

    return (
        <DropdownMenu.Item onClick={() => onSelect?.()} disabled={!contact.stats?.contributions}>
            Export transactions
        </DropdownMenu.Item>
    );
};

const MergeContact = () => {
    return (
        <Suspense fallback={<DropdownMenu.Item disabled={true}>Merge contact</DropdownMenu.Item>}>
            <MergeContactDialog.Root>
                <MergeContactDialog.Trigger asChild={true}>
                    <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                        Merge contact
                    </DropdownMenu.Item>
                </MergeContactDialog.Trigger>
            </MergeContactDialog.Root>
        </Suspense>
    );
};

const ToggleArchived = () => {
    const toast = useToast();
    const { data: contact } = useCurrentContact();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const isArchived = !!contact.archived_at;

    const { mutate: unarchiveContact } = useMutation({
        mutationFn: () => postContactArchive(accountId, contact.id, { isArchived: true }),
        onSuccess: () => {
            toast.success('Unarchived successfully');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
        },
        onError: () => {
            toast.error(`Unable to unarchive`);
        },
    });

    return !isArchived ? (
        <ArchiveContact.Root>
            <ArchiveContact.Trigger asChild>
                <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                    Archive contact
                </DropdownMenu.Item>
            </ArchiveContact.Trigger>
        </ArchiveContact.Root>
    ) : (
        <DropdownMenu.Item onClick={() => unarchiveContact()}>Unarchive contact</DropdownMenu.Item>
    );
};

const Delete = () => {
    const { data: contact } = useCurrentContact();
    const cantDeleteReasons = Object.keys(contact.cant_delete_reason || {}).reduce((all, key) => {
        if (!contact.cant_delete_reason[key as keyof typeof contact.cant_delete_reason]) {
            return all;
        }
        const reason = {
            has_bidders: 'have associated auction bids',
            has_campaign_members: 'are a member of a campaign',
            has_tickets: 'have associated tickets',
            has_transaction_plans: 'have a recurring transaction plan',
            has_undeletable_transactions: 'have associated online transactions',
            has_pledges: 'have associated pledges',
        }[key as keyof typeof contact.cant_delete_reason];

        return [...all, reason];
    }, [] as string[]);

    return contact.cant_delete ? (
        <Tooltip.Root>
            <Tooltip.Trigger asChild>
                <DropdownMenu.Item disabled onSelect={e => e.preventDefault()}>
                    Delete contact
                </DropdownMenu.Item>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content inverse>
                    <Tooltip.Arrow inverse />
                    <p>
                        This contact cannot be deleted because they:
                        <ul className="list-inside list-disc">
                            {cantDeleteReasons.map(reason => (
                                <li key={reason}>{reason}</li>
                            ))}
                        </ul>
                    </p>
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip.Root>
    ) : (
        <DeleteContact.Root>
            <DeleteContact.Trigger asChild>
                <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                    Delete contact
                </DropdownMenu.Item>
            </DeleteContact.Trigger>
        </DeleteContact.Root>
    );
};

const ContactSharedActions = ({ children, ...props }: DropdownMenu.DropdownMenuProps) => {
    const { data: contact } = useCurrentContact();

    return (
        <DropdownMenu.Root key={contact.id} {...props}>
            <DropdownMenu.Trigger asChild={true}>
                <Button variant={'outline'} color={'neutral'}>
                    Actions
                    <FontAwesomeIcon icon={faChevronDown} />
                </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content sideOffset={5} align={'end'}>
                {children}
            </DropdownMenu.Content>
        </DropdownMenu.Root>
    );
};

const Root = ContactSharedActions;

export {
    Root,
    //
    ContactSharedActions,
    AddTransaction,
    ExportTransactions,
    MergeContact,
    ToggleArchived,
    Delete,
};
